import React, { useRef, useState } from "react"
import PropTypes from "prop-types"
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from "@mui/material"
import {
    CloseOutlined,
    CloudUploadOutlined,
    DownloadOutlined,
    InsertDriveFileOutlined,
    WarningAmberOutlined
} from "@mui/icons-material"
import { Message, toaster } from "rsuite"
import _ from "lodash"
import {
    useDownloadTimetableTemplateMutation,
    useImportTimetableMutation
} from "../Redux/actions/examAction"

const STATUS = {
    new: { label: "New", color: "#047857", bg: "#ecfdf5" },
    update: { label: "Update", color: "#1d4ed8", bg: "#eff6ff" },
    unchanged: { label: "No change", color: "#475569", bg: "#f1f5f9" },
    skipped: { label: "Skipped", color: "#64748b", bg: "#f8fafc" },
    error: { label: "Error", color: "#b91c1c", bg: "#fef2f2" }
}

const errorText = (error, fallback) => {
    const detail = _.get(error, "data.detail")
    if (_.isString(detail)) return detail
    if (Array.isArray(detail)) return _.get(detail, "[0].msg", fallback)
    return fallback
}

const notify = (type, message) =>
    toaster.push(<Message showIcon type={type}>{message}</Message>, { placement: "topCenter" })

export default function ImportTimetableDialog({ exam, className, title, onClose, onImported }) {
    const inputRef = useRef(null)
    const [file, setFile] = useState(null)
    const [mode, setMode] = useState("merge")
    const [preview, setPreview] = useState(null)
    const [dragging, setDragging] = useState(false)

    const [downloadTemplate, downloadState] = useDownloadTimetableTemplateMutation()
    const [importTimetable, importState] = useImportTimetableMutation()
    const busy = importState.isLoading

    const runPreview = async (nextFile, nextMode) => {
        if (!nextFile) return
        setPreview(null)
        try {
            const result = await importTimetable({
                exam_id: exam.id,
                file: nextFile,
                dry_run: true,
                mode: nextMode
            }).unwrap()
            setPreview(result)
        } catch (error) {
            notify("error", errorText(error, "Could not read the file."))
            setFile(null)
        }
    }

    const chooseFile = (nextFile) => {
        if (!nextFile) return
        if (!/\.(xlsx|xls|csv)$/i.test(nextFile.name)) {
            notify("warning", "Choose an .xlsx or .csv file")
            return
        }
        setFile(nextFile)
        runPreview(nextFile, mode)
    }

    const changeMode = (_event, nextMode) => {
        if (!nextMode) return
        setMode(nextMode)
        runPreview(file, nextMode)
    }

    const handleDownload = async () => {
        try {
            const blob = await downloadTemplate(exam.id).unwrap()
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement("a")
            link.href = url
            link.download = `${exam.exam_name} ${className || ""} timetable.xlsx`.replace(/\s+/g, " ").trim()
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)
        } catch (error) {
            notify("error", errorText(error, "Could not download the template."))
        } finally {
            downloadState.reset()
        }
    }

    const handleImport = async () => {
        try {
            const result = await importTimetable({
                exam_id: exam.id,
                file,
                dry_run: false,
                mode
            }).unwrap()
            notify("success", result.message || "Timetable imported")
            onImported && onImported(result)
            onClose()
        } catch (error) {
            notify("error", errorText(error, "Could not import the timetable."))
        }
    }

    const summary = _.get(preview, "summary", {})
    const rows = _.get(preview, "rows", [])
    const removed = _.get(preview, "removed_subjects", [])
    const changes = (summary.new || 0) + (summary.update || 0) + (summary.removed || 0)
    const canImport = preview && !busy && !summary.errors && changes > 0

    return (
        <Dialog
            open
            onClose={busy ? undefined : onClose}
            fullWidth
            maxWidth="md"
            PaperProps={{ sx: { borderRadius: "18px" } }}
        >
            <DialogTitle sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, pb: 1 }}>
                <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: 20 }}>
                        {title || "Import timetable"}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                        {exam.exam_name}{className ? ` · ${className}` : ""}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} disabled={busy} aria-label="Close">
                    <CloseOutlined fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

                {/* Step 1: template */}
                <Box sx={{
                    display: "flex",
                    alignItems: { xs: "flex-start", sm: "center" },
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: "12px",
                    bgcolor: "#f8fafc"
                }}>
                    <Typography sx={{ fontSize: 13, color: "text.secondary", flex: 1 }}>
                        <strong>1.</strong> Download the template. It lists this class's subjects and any schedule
                        already saved. Fill in the date (YYYY-MM-DD) and start/end times (HH:MM).
                    </Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DownloadOutlined />}
                        onClick={handleDownload}
                        disabled={downloadState.isLoading}
                        sx={{ textTransform: "none", fontWeight: 600, borderRadius: "10px", whiteSpace: "nowrap" }}
                    >
                        {downloadState.isLoading ? "Preparing…" : "Download template"}
                    </Button>
                </Box>

                {/* Step 2: file */}
                <Box
                    onClick={() => !busy && inputRef.current && inputRef.current.click()}
                    onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={(e) => {
                        e.preventDefault()
                        setDragging(false)
                        chooseFile(e.dataTransfer.files[0])
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if ((e.key === "Enter" || e.key === " ") && inputRef.current) inputRef.current.click()
                    }}
                    sx={{
                        p: 3,
                        textAlign: "center",
                        cursor: busy ? "default" : "pointer",
                        borderRadius: "14px",
                        border: "2px dashed",
                        borderColor: dragging ? "primary.main" : "#cbd5e1",
                        bgcolor: dragging ? "#eef2ff" : "#fff",
                        transition: "all .15s ease",
                        "&:hover": { borderColor: "primary.main" }
                    }}
                >
                    <input
                        ref={inputRef}
                        hidden
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        onChange={(e) => {
                            chooseFile(e.target.files[0])
                            e.target.value = ""
                        }}
                    />
                    {file ? (
                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                            <InsertDriveFileOutlined color="primary" />
                            <Typography sx={{ fontWeight: 600 }}>{file.name}</Typography>
                            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>· click to change</Typography>
                        </Box>
                    ) : (
                        <>
                            <CloudUploadOutlined sx={{ fontSize: 36, color: "primary.main" }} />
                            <Typography sx={{ fontWeight: 600, mt: 0.5 }}>
                                <strong>2.</strong> Drop the filled file here, or click to choose
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                                .xlsx or .csv with Subject, Date, Start Time and End Time columns
                            </Typography>
                        </>
                    )}
                </Box>

                {/* Mode */}
                <Box sx={{ display: "flex", alignItems: { xs: "flex-start", sm: "center" }, flexDirection: { xs: "column", sm: "row" }, gap: 1.5 }}>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={mode}
                        onChange={changeMode}
                        disabled={busy}
                        sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 600, px: 2 } }}
                    >
                        <ToggleButton value="merge">Add &amp; update</ToggleButton>
                        <ToggleButton value="replace">Replace timetable</ToggleButton>
                    </ToggleButtonGroup>
                    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                        {mode === "merge"
                            ? "Subjects not in the file keep their current schedule."
                            : "Subjects not in the file are removed from this exam's timetable."}
                    </Typography>
                </Box>

                {busy && !preview && <LinearProgress />}

                {/* Preview */}
                {preview && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                            {[
                                ["new", summary.new],
                                ["update", summary.update],
                                ["unchanged", summary.unchanged],
                                ["skipped", summary.skipped],
                                ["error", summary.errors]
                            ].filter(([, count]) => count).map(([key, count]) => (
                                <Chip
                                    key={key}
                                    size="small"
                                    label={`${count} ${STATUS[key].label.toLowerCase()}`}
                                    sx={{ fontWeight: 600, color: STATUS[key].color, bgcolor: STATUS[key].bg }}
                                />
                            ))}
                            {removed.length > 0 && (
                                <Chip
                                    size="small"
                                    label={`${removed.length} removed`}
                                    sx={{ fontWeight: 600, color: "#b45309", bgcolor: "#fffbeb" }}
                                />
                            )}
                        </Box>

                        {summary.errors > 0 && (
                            <Box sx={{ display: "flex", gap: 1, p: 1.5, borderRadius: "12px", bgcolor: "#fef2f2", color: "#991b1b" }}>
                                <WarningAmberOutlined fontSize="small" />
                                <Typography sx={{ fontSize: 13 }}>
                                    Fix the {summary.errors === 1 ? "row" : `${summary.errors} rows`} marked Error in your file, then choose it again. Nothing has been saved.
                                </Typography>
                            </Box>
                        )}

                        {removed.length > 0 && (
                            <Typography sx={{ fontSize: 13, color: "#b45309" }}>
                                Will be removed: {removed.join(", ")}
                            </Typography>
                        )}

                        {rows.length === 0 ? (
                            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                                The file has no rows.
                            </Typography>
                        ) : (
                            <TableContainer sx={{ maxHeight: 320, border: "1px solid #e2e8f0", borderRadius: "12px" }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow sx={{ "& th": { fontWeight: 700, fontSize: 12, color: "text.secondary", bgcolor: "#f8fafc" } }}>
                                            <TableCell>Row</TableCell>
                                            <TableCell>Subject</TableCell>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Time</TableCell>
                                            <TableCell>Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rows.map((row) => {
                                            const status = STATUS[row.status] || STATUS.error
                                            const notes = [...(row.errors || []), ...(row.warnings || [])]
                                            return (
                                                <TableRow key={row.row} sx={{ bgcolor: row.status === "error" ? "#fffafa" : "inherit" }}>
                                                    <TableCell sx={{ color: "text.secondary" }}>{row.row}</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>{row.subject || "—"}</TableCell>
                                                    <TableCell>{row.date ? new Date(`${row.date}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—"}</TableCell>
                                                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                        {row.start && row.end ? `${row.start} – ${row.end}` : "—"}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            size="small"
                                                            label={status.label}
                                                            sx={{ fontWeight: 600, fontSize: 11, color: status.color, bgcolor: status.bg }}
                                                        />
                                                        {notes.map((note) => (
                                                            <Typography
                                                                key={note}
                                                                sx={{
                                                                    fontSize: 12,
                                                                    mt: 0.5,
                                                                    color: (row.errors || []).includes(note) ? "#b91c1c" : "#b45309"
                                                                }}
                                                            >
                                                                {note}
                                                            </Typography>
                                                        ))}
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                <Button onClick={onClose} disabled={busy} sx={{ textTransform: "none", fontWeight: 600 }}>
                    {title ? "Later" : "Cancel"}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleImport}
                    disabled={!canImport}
                    startIcon={<CloudUploadOutlined />}
                    sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", px: 2.5, boxShadow: "none" }}
                >
                    {busy && preview
                        ? "Importing…"
                        : preview && changes === 0 && !summary.errors
                            ? "Nothing to change"
                            : `Import${changes ? ` ${changes} change${changes === 1 ? "" : "s"}` : ""}`}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

ImportTimetableDialog.propTypes = {
    exam: PropTypes.shape({
        id: PropTypes.number.isRequired,
        exam_name: PropTypes.string
    }).isRequired,
    className: PropTypes.string,
    title: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    onImported: PropTypes.func
}
